import React, { useContext, useState, useEffect, useMemo } from 'react';
import { useParams, useHistory, useLocation, useRouteMatch, Route } from 'react-router-dom';
import { format, parse } from 'date-fns';

// Infrastructure
import { EventDetailProvider } from '../../../../icsc-app/js/components/pages/MyIcsc/Events/EventDetailProvider';
import useEventSessionsTabsStartDate from '../../../../icsc-app/js/hooks/useEventSessionsTabsStartDate';

// Components
import SessionsSearch from '../searches/SessionsSearch';
import SessionDaySidebar from '../_organisms/SessionDaySidebar';
import EventTrackSelection from '../../../../icsc-app/js/components/_molecules/EventTrackSelection';
import SessionSidebar from '../_organisms/SessionsSidebar';
import LoadingArea from '../../../../_shared-react/components/_molecules/LoadingArea';

const SessionsView = ({ baseUrl }) => {
    const { event } = useContext(EventDetailProvider);
    const { eventSessionsTabsStartDate } = useEventSessionsTabsStartDate(event);

    const { date, sessionSlug } = useParams();
    const history = useHistory();
    const location = useLocation();

    const [selectedTracks, setSelectedTracks] = useState([]);
    const [globalTracks, setGlobalTracks] = useState([]);
    const [hasLoadedTracks, setHasLoadedTracks] = useState(false);

    const activeDay = useMemo(() => {
        const dateRegex = /^\d{4}-\d{2}-\d{2}$/;
        if (date && dateRegex.test(date)) {
            try {
                const parsedDate = parse(date, 'yyyy-MM-dd', new Date());
                if (!isNaN(parsedDate.getTime())) return parsedDate;
            } catch (e) {
                console.error("Invalid date format in URL:", date);
            }
        }
        return eventSessionsTabsStartDate || new Date();
    }, [date, eventSessionsTabsStartDate]);

    useEffect(() => {
        const params = new URLSearchParams(location.search);
        const tracks = params.get('tracks');
        setSelectedTracks(tracks ? tracks.split(',') : []);
    }, [location.search]);

    const updateUrl = (newDate, newTracks) => {
        const dateStr = format(newDate, 'yyyy-MM-dd');
        const params = new URLSearchParams(location.search);

        if (newTracks && newTracks.length > 0) {
            params.set('tracks', newTracks.join(','));
        } else {
            params.delete('tracks');
        }

        const queryString = params.toString();
        let newPath = `${baseUrl}/${dateStr}`;

        if (sessionSlug) {
            newPath += `/${sessionSlug}`;
        }

        history.push({
            pathname: newPath,
            search: queryString ? `?${queryString}` : ''
        });
    };

    if (!hasLoadedTracks) {
        return (
            <>
                <div style={{ position: 'absolute', opacity: 0, height: 0 }}>
                    <SessionsSearch
                        event={event}
                        activeDay={null}
                        selectedTracks={[]}
                        isGlobal={true}
                        setHasLoaded={setHasLoadedTracks}
                        setAvailableTracks={setGlobalTracks}
                    />
                </div>
                <LoadingArea />
            </>
        );
    }

    return (
        <div className="sessions-container">
            <SessionSidebar>
                <SessionDaySidebar
                    activeDay={activeDay}
                    eventStartDate={eventSessionsTabsStartDate}
                    eventEndDate={event?.meeting_end_time}
                    onDayChange={(d) => updateUrl(d, selectedTracks)}
                    event={event}
                />
            </SessionSidebar>

            <div className="sessions-main-content py-2 px-md-4">
                <div className="sessions-desktop-header">
                    <div className="sessions-label">Schedule for</div>
                    <div className="sessions-title">{event?.title || 'Event Schedule'}</div>
                    {activeDay instanceof Date && !isNaN(activeDay.getTime()) && (
                        <div className="sessions-selected-date">
                            {format(activeDay, 'EEEE, MMMM d, yyyy')}
                        </div>
                    )}
                </div>

                {hasLoadedTracks && globalTracks.length > 0 && (
                    <EventTrackSelection
                        tracks={globalTracks}
                        selectedTracks={selectedTracks}
                        onToggleTrack={(id) => {
                            const nextTracks = selectedTracks.includes(id)
                                ? selectedTracks.filter(t => t !== id)
                                : [...selectedTracks, id];
                            updateUrl(activeDay, nextTracks);
                        }}
                        onClearAll={() => updateUrl(activeDay, [])}
                    />
                )}

                <SessionsSearch
                    event={event}
                    activeDay={activeDay}
                    selectedTracks={selectedTracks}
                    sessionSlug={sessionSlug}
                />
            </div>
        </div>
    );
};

const SessionsPage = () => {
    const match = useRouteMatch();
    return (
        <Route path={`${match.path}/:date?/:sessionSlug?`}>
            <SessionsView baseUrl={match.url} />
        </Route>
    );
};

export default SessionsPage;
